import {
  CodeSandboxOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { Button, Collapse, Tooltip } from 'antd'
import {
  convertKeyToLabel,
  ONTOLOGY_FIELD_INPUTS,
  OntologyField,
  OntologyFieldDefinition,
  OntologyFieldType,
  OntologyValueType,
} from 'features/dynamicOntology'
import isPlainObject from 'lodash/isPlainObject'
import omit from 'lodash/omit'
import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { RolodexHistogramFilter } from 'types'

import { AddPropertyButton, getInitialValueForType } from './AddPropertyButton'
import { EditableLabel } from './EditableLabel'
import {
  inferPropertyType,
  PropertyType,
  PropertyTypeSelect,
} from './PropertyTypeSelect'

const INDENT_PER_LEVEL = 0.5
const PROPERTY_GRID_COLUMNS = 'minmax(12rem, 0.38fr) minmax(0, 1fr)'

function resolveSetState<T>(action: SetStateAction<T>, prevValue: T): T {
  return typeof action === 'function'
    ? (action as (prevState: T) => T)(prevValue)
    : action
}

const getPrimitiveLeafValue = (
  value: OntologyValueType | undefined
): string | number | null | undefined => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value === null
  ) {
    return value
  }

  return undefined
}

export function getNextKey(
  prefix: string,
  object: Record<string, unknown>
): string {
  const keys = Object.keys(object ?? {})
  const maxSuffix = keys.reduce((max, key) => {
    if (!key.startsWith(prefix)) {
      return max
    }
    const numericSuffix = Number.parseInt(key.slice(prefix.length), 10)
    if (Number.isNaN(numericSuffix)) {
      return max
    }
    return Math.max(max, numericSuffix)
  }, 0)

  return `${prefix}${maxSuffix + 1}`
}

const getLabelKeyPrefix = (propertyKey?: string): string => {
  if (!propertyKey) {
    return ''
  }

  const lastDotIndex = propertyKey.lastIndexOf('.')

  return lastDotIndex >= 0 ? propertyKey.slice(0, lastDotIndex + 1) : ''
}

export const PropertyField = styled.div<{
  $indent?: number
  $layout?: 'field' | 'group'
}>`
  display: ${({ $layout = 'group' }) =>
    $layout === 'field' ? 'grid' : 'flex'};
  ${({ $layout = 'group' }) =>
    $layout === 'field'
      ? `grid-template-columns: ${PROPERTY_GRID_COLUMNS};`
      : 'flex-direction: column;'}
  align-items: ${({ $layout = 'group' }) =>
    $layout === 'field' ? 'start' : 'stretch'};
  gap: ${({ $layout = 'group' }) => ($layout === 'field' ? '1rem' : '0.25rem')};
  margin-left: ${({ $indent = 0, $layout = 'group' }) =>
    $layout === 'field' ? '0' : `${$indent}rem`};
  margin-bottom: ${({ $layout = 'group' }) =>
    $layout === 'field' ? '0' : '0.75rem'};
  padding: ${({ $layout = 'group' }) =>
    $layout === 'field' ? '0.625rem 0.75rem' : '0'};
  border-bottom: ${({ $layout = 'group', theme }) =>
    $layout === 'field'
      ? `1px solid ${theme.forecast.cardDividerBackground}`
      : 'none'};
  break-inside: avoid;
  width: 100%;
  min-width: 0;

  @media (max-width: 700px) {
    ${({ $layout = 'group' }) =>
      $layout === 'field' &&
      `
        grid-template-columns: 1fr;
        gap: 0.375rem;
      `}
  }
`

export const PropertyLabel = styled.label`
  color: ${CantileverTheme.colors.gray['400']};
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.25;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0;
  margin-left: 0.2rem;
  padding-top: 0.4375rem;
`

export const PropertyValue = styled.div`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  min-height: 2.25rem;
  display: flex;
  align-items: center;
  word-break: break-word;
`

export const NestedContainer = styled.div`
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const EntryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
`

const PropertiesTable = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.5rem;
  overflow: hidden;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
`

const PropertiesTableHeader = styled.div`
  display: grid;
  grid-template-columns: ${PROPERTY_GRID_COLUMNS};
  gap: 1rem;
  padding: 0.5rem 0.75rem;
  background: ${({ theme }) => theme.forecast.background3};
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
  color: ${CantileverTheme.colors.gray['400']};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  @media (max-width: 700px) {
    display: none;
  }
`

const PropertiesTableBody = styled.div`
  ${EntryRow}:last-child ${PropertyField} {
    border-bottom: none;
  }
`

const StyledCollapse = styled(Collapse)`
  width: 100%;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-color: ${({ theme }) => theme.forecast.cardDividerBackground};

  > .ant-collapse-item {
    border-color: ${({ theme }) => theme.forecast.cardDividerBackground};
  }

  > .ant-collapse-item > .ant-collapse-header {
    align-items: center;
    background: transparent;
    color: ${({ theme }) => theme.forecast.defaultText};
    padding: 0.5rem 0.75rem;
  }

  > .ant-collapse-item > .ant-collapse-content {
    background: ${({ theme }) => theme.forecast.cardGroupBackground};
    border-color: ${({ theme }) => theme.forecast.cardDividerBackground};
  }

  > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
    padding: 0;
  }
`

const CollapseHeader = styled.span`
  color: ${CantileverTheme.colors.gray['400']};
  font-size: 0.8125rem;
`

const NestedBranchContainer = styled.div`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};

  &:last-child {
    border-bottom: none;
  }
`

const NestedBranchHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.8125rem;
  font-weight: 600;
`

const AddButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
`

const getArraySummary = (count: number): string =>
  count === 1 ? '1 item' : `${count} items`

const getObjectSummary = (count: number): string =>
  count === 1 ? '1 property' : `${count} properties`

const CollapsiblePropertyValue: FC<{
  summary: string
  children: ReactNode
}> = ({ summary, children }) => (
  <StyledCollapse
    size='small'
    items={[
      {
        key: 'content',
        label: <CollapseHeader>{summary}</CollapseHeader>,
        children,
      },
    ]}
  />
)

type ObjectSetter = Dispatch<SetStateAction<OntologyValueType | undefined>>

const createArraySetter = (
  setObject: ObjectSetter,
  index: number,
  currentValue?: OntologyValueType
): Dispatch<SetStateAction<OntologyValueType | undefined>> => {
  return (val) => {
    setObject((prevObject: OntologyValueType | undefined) => {
      const prevArray = Array.isArray(prevObject)
        ? (prevObject as ReadonlyArray<OntologyValueType>)
        : undefined
      const previousValue =
        prevArray && index < prevArray.length ? prevArray[index] : currentValue
      const nextValue = resolveSetState(
        val,
        previousValue as OntologyValueType | undefined
      )

      if (nextValue === undefined) {
        if (!prevArray || index >= prevArray.length) {
          return prevObject
        }
        const nextArray = prevArray.filter((_, i) => i !== index)
        return nextArray as unknown as OntologyValueType
      }

      const nextArray: Array<OntologyValueType> = prevArray
        ? [...prevArray]
        : []

      nextArray[index] = nextValue

      return nextArray as unknown as OntologyValueType
    })
  }
}

const createNestedRecordSetter = (
  setObject: ObjectSetter,
  key: string,
  currentValue?: OntologyValueType
): Dispatch<SetStateAction<OntologyValueType | undefined>> => {
  return (val) => {
    setObject((prevObject: OntologyValueType | undefined) => {
      const isRecord = isPlainObject(prevObject) && !Array.isArray(prevObject)
      const prevRecord: Record<string, OntologyValueType | undefined> = isRecord
        ? {
            ...(prevObject as Record<string, OntologyValueType | undefined>),
          }
        : {}
      const hasPrevKey = Object.prototype.hasOwnProperty.call(prevRecord, key)
      const previousValue = hasPrevKey ? prevRecord[key] : currentValue
      const nextValue = resolveSetState(
        val,
        previousValue as OntologyValueType | undefined
      )

      if (nextValue === undefined) {
        if (!hasPrevKey) {
          return prevObject
        }
        return omit(prevRecord, key)
      }

      return {
        ...prevRecord,
        [key]: nextValue,
      }
    })
  }
}

const createNestedLabelSetter = (
  setObject: ObjectSetter,
  key: string
): Dispatch<SetStateAction<string>> => {
  return (action) => {
    setObject((prevObject: OntologyValueType | undefined) => {
      if (!isPlainObject(prevObject) || Array.isArray(prevObject)) {
        return prevObject
      }

      const prevRecord = prevObject as Record<string, OntologyValueType>

      if (!Object.prototype.hasOwnProperty.call(prevRecord, key)) {
        return prevObject
      }

      const rawNextKey = resolveSetState(action, key)
      const nextKey = rawNextKey?.trim()

      if (!nextKey || nextKey === key) {
        return prevObject
      }

      if (Object.prototype.hasOwnProperty.call(prevRecord, nextKey)) {
        return prevObject
      }

      const entries = Object.entries(prevRecord)
      const nextEntries = entries.map(([entryKey, entryValue]) =>
        entryKey === key ? [nextKey, entryValue] : [entryKey, entryValue]
      ) as Array<[string, OntologyValueType | undefined]>

      const nextRecord = nextEntries.reduce<
        Record<string, OntologyValueType | undefined>
      >((acc, [entryKey, entryValue]) => {
        acc[entryKey] = entryValue
        return acc
      }, {})

      return nextRecord as unknown as OntologyValueType
    })
  }
}

const LeafContainer = styled.div<{ $isEditing?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;

  .ant-input,
  .ant-input-number,
  .ant-picker,
  .ant-select {
    width: 100%;
  }

  ${({ $isEditing, theme }) =>
    !$isEditing &&
    `
      .ant-input,
      .ant-input-number,
      .ant-picker {
        background: transparent;
        border-color: transparent;
        box-shadow: none;
        color: ${theme.forecast.defaultText};
      }

      .ant-input,
      .ant-input-number-input,
      .ant-picker-input > input {
        padding-left: 0;
      }
    `}
`

const LeafActionsContainer = styled.div`
  display: flex;
  align-items: center;
`

const LEAF_ALLOWED_TYPES: ReadonlyArray<OntologyFieldType> = [
  'text',
  'number',
  'boolean',
  'enum',
  'date',
]

const LeafFieldContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  width: 100%;
`

const StyledInfoIcon = styled(InfoCircleOutlined)`
  color: ${CantileverTheme.colors.gray['400']};
`

const PropertyLeafView: FC<RecursivePropertyViewProps & { indent: number }> = ({
  label,
  propertyKey,
  setLabel,
  indent,
  object,
  setObject,
  field,
  fieldDef,
  filters,
  leafActions,
  style,
  multilineText,
}) => {
  const [overrideFieldType, setOverrideFieldType] = useState<
    PropertyType | undefined
  >(undefined)
  const inferredFieldType = useMemo<PropertyType>(
    () =>
      overrideFieldType ??
      fieldDef?.type ??
      inferPropertyType(object) ??
      'text',
    [overrideFieldType, fieldDef?.type, object]
  )

  const LeafComponent = useMemo(() => {
    return ONTOLOGY_FIELD_INPUTS[inferredFieldType]
  }, [inferredFieldType])

  const safeSetObject = useCallback(
    (value: OntologyValueType | undefined) => {
      setObject?.(value ?? null)
    },
    [setObject]
  )

  const safeSetOverrideFieldType = useCallback(
    (type: PropertyType) => {
      setOverrideFieldType((prev) => {
        if (prev === type) {
          return prev
        }
        safeSetObject?.(undefined)
        return type
      })
    },
    [safeSetObject, setOverrideFieldType]
  )

  const primitiveLeafValue = useMemo(
    () => getPrimitiveLeafValue(object),
    [object]
  )
  const leafActionSetter = useMemo<
    Dispatch<SetStateAction<string | number | null>> | undefined
  >(() => {
    if (!setObject) {
      return undefined
    }

    return (valueOrUpdater) => {
      if (typeof valueOrUpdater === 'function') {
        setObject((prevValue) => {
          const updater = valueOrUpdater as (
            prevState: string | number | null
          ) => string | number | null
          const previousPrimitive =
            getPrimitiveLeafValue(prevValue) ?? primitiveLeafValue ?? null

          return updater(previousPrimitive)
        })
        return
      }

      setObject(valueOrUpdater)
    }
  }, [setObject, primitiveLeafValue])

  const labelKeyPrefix = useMemo(
    () => getLabelKeyPrefix(propertyKey),
    [propertyKey]
  )

  if (!LeafComponent) {
    return null
  }

  return (
    <LeafFieldContainer>
      <PropertyField $indent={indent} $layout='field'>
        <PropertyLabel>
          <EditableLabel
            keyPrefix={labelKeyPrefix}
            label={label}
            propertyKey={propertyKey}
            setLabel={setLabel}
            disableEdit={Boolean(fieldDef)}
          />
          {fieldDef?.description && (
            <Tooltip title={fieldDef.description}>
              <StyledInfoIcon />
            </Tooltip>
          )}
        </PropertyLabel>
        <LeafContainer $isEditing={Boolean(setObject)}>
          <LeafComponent
            value={object}
            onChange={setObject ? safeSetObject : undefined}
            field={field}
            fieldDef={fieldDef}
            filters={filters}
            style={style}
            multiline={multilineText}
          />
          {leafActions && (
            <LeafActionsContainer>
              {leafActions({
                field: propertyKey ?? label ?? '',
                object: primitiveLeafValue as OntologyValueType,
                setObject: leafActionSetter as Dispatch<
                  SetStateAction<OntologyValueType>
                >,
                fieldDef,
              })}
            </LeafActionsContainer>
          )}
          {!fieldDef && setObject && (
            <PropertyTypeSelect
              selectedType={inferredFieldType}
              setSelectedType={safeSetOverrideFieldType}
              allowedTypes={LEAF_ALLOWED_TYPES}
            />
          )}
        </LeafContainer>
      </PropertyField>
    </LeafFieldContainer>
  )
}

export interface LeafActionsParams {
  field: string
  object: OntologyValueType
  setObject?: Dispatch<SetStateAction<OntologyValueType>>
  fieldDef?: OntologyFieldDefinition
}

export interface RecursivePropertyViewProps {
  keyPrefix?: string
  label: string | undefined
  propertyKey?: string
  object: OntologyValueType | undefined
  setObject?: Dispatch<SetStateAction<OntologyValueType | undefined>>
  setLabel?: Dispatch<SetStateAction<string>>
  indentLevel?: number
  field?: OntologyField
  fieldDef?: OntologyFieldDefinition
  ontologyFields?: Record<string, [string, OntologyFieldDefinition]>
  filters?: ReadonlyArray<RolodexHistogramFilter>
  leafActions?: (params: LeafActionsParams) => ReactNode
  style?: React.CSSProperties
  multilineText?: boolean
}

const RecursiveArrayPropertyView: FC<RecursivePropertyViewProps> = ({
  label,
  propertyKey,
  object,
  indentLevel = 0,
  setObject,
  setLabel,
  fieldDef,
  keyPrefix = '',
  field,
  ontologyFields,
  filters,
  leafActions,
  ...rest
}) => {
  const indent = indentLevel * INDENT_PER_LEVEL
  const values = object as ReadonlyArray<OntologyValueType>

  const itemData = useMemo(
    () =>
      values.map((value, idx) => {
        const setter = setObject
          ? createArraySetter(setObject, idx, value as OntologyValueType)
          : undefined
        return {
          keyPrefix: keyPrefix + idx + '.',
          setter,
          deleteHandler: setter ? () => setter(undefined) : undefined,
        }
      }),
    [keyPrefix, values, setObject]
  )

  const handleAddArrayValue = useCallback(
    (type: PropertyType) => {
      if (!setObject) {
        return
      }

      setObject((prevObject: OntologyValueType | undefined) => {
        const baseArray = Array.isArray(prevObject)
          ? [...(prevObject as Array<OntologyValueType>)]
          : Array.isArray(object)
            ? [...(object as Array<OntologyValueType>)]
            : []

        baseArray.push(getInitialValueForType(type))

        return baseArray as unknown as OntologyValueType
      })
    },
    [object, setObject]
  )

  const labelKeyPrefix = useMemo(
    () => getLabelKeyPrefix(propertyKey),
    [propertyKey]
  )

  const renderedItems = useMemo(
    () =>
      values.length > 0 ? (
        values.map((value, idx) => {
          const {
            keyPrefix: itemKeyPrefix,
            setter: nestedSetter,
            deleteHandler,
          } = itemData[idx]

          return (
            <EntryRow key={idx}>
              {nestedSetter && !fieldDef && (
                <Button
                  icon={<DeleteOutlined />}
                  type='text'
                  danger
                  size='small'
                  onClick={deleteHandler}
                />
              )}
              <RecursivePropertyView
                keyPrefix={itemKeyPrefix}
                label={`Item ${idx + 1}`}
                object={value as OntologyValueType}
                indentLevel={indentLevel + 1}
                setObject={nestedSetter}
                fieldDef={fieldDef}
                field={field}
                ontologyFields={ontologyFields}
                filters={filters}
                leafActions={leafActions}
                {...rest}
              />
            </EntryRow>
          )
        })
      ) : (
        <EmptyListSplash />
      ),
    [
      field,
      fieldDef,
      filters,
      indentLevel,
      itemData,
      leafActions,
      ontologyFields,
      rest,
      values,
    ]
  )

  if (indentLevel > 0) {
    return (
      <NestedBranchContainer>
        <StyledCollapse
          size='small'
          items={[
            {
              key: 'content',
              label: (
                <NestedBranchHeader>
                  <EditableLabel
                    keyPrefix={labelKeyPrefix}
                    label={label}
                    propertyKey={propertyKey}
                    setLabel={setLabel}
                    disableEdit={Boolean(fieldDef)}
                  />
                  <CollapseHeader>
                    {getArraySummary(values.length)}
                  </CollapseHeader>
                </NestedBranchHeader>
              ),
              children: (
                <NestedContainer>
                  {renderedItems}
                  {setObject && !fieldDef && (
                    <AddButtonContainer>
                      <AddPropertyButton onAddProperty={handleAddArrayValue} />
                    </AddButtonContainer>
                  )}
                </NestedContainer>
              ),
            },
          ]}
        />
      </NestedBranchContainer>
    )
  }

  return (
    <PropertyField $indent={indent} $layout='field'>
      <PropertyLabel>
        <EditableLabel
          keyPrefix={labelKeyPrefix}
          label={label}
          propertyKey={propertyKey}
          setLabel={setLabel}
          disableEdit={Boolean(fieldDef)}
        />
      </PropertyLabel>
      <CollapsiblePropertyValue summary={getArraySummary(values.length)}>
        <NestedContainer>
          {renderedItems}
          {setObject && !fieldDef && (
            <AddButtonContainer>
              <AddPropertyButton onAddProperty={handleAddArrayValue} />
            </AddButtonContainer>
          )}
        </NestedContainer>
      </CollapsiblePropertyValue>
    </PropertyField>
  )
}

const RecursiveObjectPropertyView: FC<RecursivePropertyViewProps> = ({
  keyPrefix = '',
  label,
  propertyKey,
  object,
  indentLevel = 0,
  setObject,
  setLabel,
  ontologyFields,
  fieldDef,
  leafActions,
  ...rest
}) => {
  const indent = indentLevel * INDENT_PER_LEVEL
  const entries = useMemo(
    () => Object.entries(object as Record<string, unknown>),
    [object]
  )

  const entryData = useMemo(
    () =>
      entries.map(([key, value]) => {
        const [childField, childFieldDef] = ontologyFields?.[key] ?? [
          undefined,
          undefined,
        ]
        const nestedSetter = setObject
          ? createNestedRecordSetter(setObject, key, value as OntologyValueType)
          : undefined
        const labelSetter =
          setObject && nestedSetter && !childFieldDef
            ? createNestedLabelSetter(setObject, key)
            : undefined

        return {
          itemKeyPrefix: keyPrefix + key + '.',
          itemPropertyKey: keyPrefix + key,
          convertedLabel: childFieldDef?.name ?? convertKeyToLabel(key),
          childField,
          childFieldDef,
          nestedSetter,
          labelSetter,
          deleteHandler: nestedSetter
            ? () => nestedSetter(undefined)
            : undefined,
        }
      }),
    [entries, keyPrefix, ontologyFields, setObject]
  )

  const handleAddObjectProperty = useCallback(
    (type: PropertyType) => {
      if (!setObject) {
        return
      }

      setObject((prevObject: OntologyValueType | undefined) => {
        const baseRecord =
          isPlainObject(prevObject) && !Array.isArray(prevObject)
            ? (prevObject as Record<string, OntologyValueType>)
            : (object as Record<string, OntologyValueType>)

        const nextRecord: Record<string, OntologyValueType> = {
          ...baseRecord,
        }

        const propertyKey = getNextKey('field_', nextRecord)
        nextRecord[propertyKey] = getInitialValueForType(type)

        return nextRecord as unknown as OntologyValueType
      })
    },
    [object, setObject]
  )

  const labelKeyPrefix = useMemo(
    () => getLabelKeyPrefix(propertyKey),
    [propertyKey]
  )

  const renderedEntries = useMemo(
    () =>
      entries.length > 0 ? (
        entries.map(([key, value], idx) => {
          const {
            itemKeyPrefix,
            itemPropertyKey,
            convertedLabel,
            childField,
            childFieldDef,
            nestedSetter,
            labelSetter,
            deleteHandler,
          } = entryData[idx]

          return (
            <EntryRow key={key}>
              {nestedSetter && !childFieldDef && (
                <Button
                  icon={<DeleteOutlined />}
                  type='text'
                  danger
                  size='small'
                  onClick={deleteHandler}
                  disabled={!nestedSetter}
                />
              )}
              <RecursivePropertyView
                keyPrefix={itemKeyPrefix}
                label={convertedLabel}
                propertyKey={itemPropertyKey}
                object={value as OntologyValueType}
                indentLevel={indentLevel === 0 ? 0 : indentLevel + 1}
                setObject={nestedSetter}
                setLabel={labelSetter}
                field={childField}
                fieldDef={childFieldDef}
                ontologyFields={ontologyFields}
                leafActions={leafActions}
                {...rest}
              />
            </EntryRow>
          )
        })
      ) : (
        <EmptyObjectSplash />
      ),
    [entries, entryData, indentLevel, leafActions, ontologyFields, rest]
  )

  if (indentLevel === 0 && label === undefined) {
    return (
      <PropertiesTable>
        <PropertiesTableHeader>
          <span>Property</span>
          <span>Value</span>
        </PropertiesTableHeader>
        <PropertiesTableBody>{renderedEntries}</PropertiesTableBody>
        {setObject && !ontologyFields && (
          <AddButtonContainer>
            <AddPropertyButton onAddProperty={handleAddObjectProperty} />
          </AddButtonContainer>
        )}
      </PropertiesTable>
    )
  }

  if (indentLevel > 0) {
    return (
      <NestedBranchContainer>
        <StyledCollapse
          size='small'
          items={[
            {
              key: 'content',
              label: (
                <NestedBranchHeader>
                  <EditableLabel
                    keyPrefix={labelKeyPrefix}
                    label={label}
                    propertyKey={propertyKey}
                    setLabel={setLabel}
                    disableEdit={Boolean(fieldDef)}
                  />
                  <CollapseHeader>
                    {getObjectSummary(entries.length)}
                  </CollapseHeader>
                </NestedBranchHeader>
              ),
              children: (
                <NestedContainer>
                  {renderedEntries}
                  {setObject && !ontologyFields && (
                    <AddButtonContainer>
                      <AddPropertyButton
                        onAddProperty={handleAddObjectProperty}
                      />
                    </AddButtonContainer>
                  )}
                </NestedContainer>
              ),
            },
          ]}
        />
      </NestedBranchContainer>
    )
  }

  return (
    <PropertyField $indent={indent} $layout='field'>
      <PropertyLabel>
        <EditableLabel
          keyPrefix={labelKeyPrefix}
          label={label}
          propertyKey={propertyKey}
          setLabel={setLabel}
          disableEdit={Boolean(fieldDef)}
        />
      </PropertyLabel>
      <CollapsiblePropertyValue summary={getObjectSummary(entries.length)}>
        <NestedContainer>
          {renderedEntries}
          {setObject && !ontologyFields && (
            <AddButtonContainer>
              <AddPropertyButton onAddProperty={handleAddObjectProperty} />
            </AddButtonContainer>
          )}
        </NestedContainer>
      </CollapsiblePropertyValue>
    </PropertyField>
  )
}

/**
 * The meat of the object properties view.
 *
 * This component renders a property view for a given object, which can be an array, object, or 'leaf' (primitive value).
 * Recursions are handled by dispatching to a RecursiveArrayPropertyView or RecursiveObjectPropertyView.
 *
 * If you supply an 'ontologyFields' prop, the components will render according to the ontology, and will prevent
 * type- and key- edits at the root level.
 *
 * If you supply a 'setObject' prop, the component will render in 'Edit' mode.
 */
const RawRecursivePropertyView: FC<RecursivePropertyViewProps> = ({
  object,
  indentLevel = 0,
  ontologyFields,
  ...rest
}) => {
  const indent = indentLevel * INDENT_PER_LEVEL

  return Array.isArray(object) ? (
    <RecursiveArrayPropertyView
      object={object}
      indentLevel={indentLevel}
      ontologyFields={ontologyFields}
      {...rest}
    />
  ) : isPlainObject(object) ? (
    <RecursiveObjectPropertyView
      object={object}
      indentLevel={indentLevel}
      ontologyFields={ontologyFields}
      {...rest}
    />
  ) : (
    <PropertyLeafView
      object={object}
      indentLevel={indentLevel}
      ontologyFields={ontologyFields}
      indent={indent}
      {...rest}
    />
  )
}
export const RecursivePropertyView = React.memo(RawRecursivePropertyView)

const NoDataSplashContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  color: ${CantileverTheme.colors.gray['400']};
  text-align: center;
`

const StyledEmptyIcon = styled.div`
  color: gray;
  font-size: 1.5rem;
`

const RawEmptyListSplash = () => (
  <NoDataSplashContainer>
    <StyledEmptyIcon as={UnorderedListOutlined} />
    Empty List
  </NoDataSplashContainer>
)
const EmptyListSplash = React.memo(RawEmptyListSplash)
EmptyListSplash.displayName = 'EmptyListSplash'

const RawEmptyObjectSplash = () => (
  <NoDataSplashContainer>
    <StyledEmptyIcon as={CodeSandboxOutlined} />
    Empty Object
  </NoDataSplashContainer>
)
const EmptyObjectSplash = React.memo(RawEmptyObjectSplash)
EmptyObjectSplash.displayName = 'EmptyObjectSplash'
